import { SenderSource } from '@enuan/chat__utils';
import React, { useContext, useMemo, JSX } from 'react';
import { configurationContext } from '../../contexts/configuration';
import classNames from 'classnames';
import { ReactComponent as AvatarM3 } from '@/assets/avatar_m3.svg';
import { ReactComponent as AvatarAgent } from '@/assets/avatar_agent.svg';
import { ReactComponent as AvatarBase } from '@/assets/avatar_base.svg';
import { styled } from '@mui/material/styles';

/**
 * Props for the `Avatar` component.
 */
type AvatarProps = JSX.IntrinsicElements['figure'] & {
	sender: SenderSource;
};

/**
 * Renders the chat message avatar for a sender.
 *
 * Display logic driven by `chat.*_logo` and the hide flags:
 * - `*_logo === false`      — entire avatar slot hidden; returns `null`.
 * - `*_logo` is a string   — custom image rendered inside the shape frame.
 * - `*_logo === undefined`  — built-in SVG icon shown unless `hide_*_avatar_icon`.
 *
 * The rounded background shape (`AvatarBase`) is rendered unless
 * `hide_*_avatar_shape` is `true`. When the shape is visible and a custom
 * image is used, the image is rendered at 70 % of the avatar slot so the
 * shape is visible as a frame around the image (same sizing as SVG icons).
 * When the shape is hidden the custom image fills the full slot (100 %).
 */
export const Avatar = styled(
	({ sender, className, ...props }: AvatarProps) => {
		const { chat } = useContext(configurationContext);
		const {
			agent_logo,
			system_logo,
			user_logo,
			hide_user_avatar_shape,
			hide_agent_avatar_shape,
			hide_system_avatar_shape,
			hide_user_avatar_icon,
			hide_agent_avatar_icon,
			hide_system_avatar_icon,
		} = chat ?? {};

		const senderLogo =
			sender === SenderSource.USER
				? user_logo
				: sender === SenderSource.AGENT
					? agent_logo
					: sender === SenderSource.SYSTEM
						? system_logo
						: undefined;

		const hideShape = shouldHideAvatarShape(sender, {
			hide_user_avatar_shape,
			hide_agent_avatar_shape,
			hide_system_avatar_shape,
		});

		const hideIcon = shouldHideAvatarIcon(sender, {
			hide_user_avatar_icon,
			hide_agent_avatar_icon,
			hide_system_avatar_icon,
		});

		/**
		 * Resolves the avatar icon element for this sender.
		 *
		 * Custom images are sized to 70 % when the shape is visible (so the frame
		 * shows around the image) and to 100 % when the shape is hidden (fills the
		 * slot). SVG icons are always 70 % — they rely on the shape as the
		 * coloured background.
		 *
		 * @returns The JSX icon element, or `null` when avatar is suppressed.
		 */
		/**
		 * Resolves the avatar icon element for this sender.
		 *
		 * Three-way distinction on `senderLogo`:
		 * - `string` (non-empty) — custom image uploaded; renders `<img>` sized to
		 *   70 % when the shape is visible (framed) or 100 % when hidden.
		 * - `string` (empty `""`) — custom mode selected but no image uploaded yet;
		 *   renders nothing so the built-in icon is not shown as a false fallback.
		 * - `undefined`           — default mode; built-in SVG icon shown unless
		 *   `hideIcon` explicitly suppresses it.
		 * (`false` is handled by the early `return null` below the memo call.)
		 *
		 * @returns The JSX icon element, or `null` when nothing should render.
		 */
		const icon = useMemo<JSX.Element | null>(() => {
			if (typeof senderLogo === 'string') {
				// Empty string = custom mode with no image uploaded yet → no icon.
				if (!senderLogo) return null;
				return (
					<img
						className={classNames('icon', sender, {
							'icon--framed': !hideShape,
						})}
						alt=""
						src={senderLogo}
					/>
				);
			}

			// `undefined` = default mode → show built-in icon unless suppressed.
			if (hideIcon) return null;

			if (sender === SenderSource.SYSTEM)
				return <AvatarM3 className={classNames('icon', 'm3')} />;
			if (sender === SenderSource.AGENT)
				return <AvatarAgent className={classNames('icon', 'agent')} />;
			if (sender === SenderSource.USER)
				return <AvatarAgent className={classNames('icon', 'user')} />;
			return null;
		}, [senderLogo, hideIcon, hideShape, sender]);

		// Entire avatar slot hidden — reserve no space.
		if (senderLogo === false) return null;

		return (
			<figure
				className={classNames(
					'message__avatar',
					`message__avatar--${sender}`,
					className,
				)}
				{...props}
			>
				{!hideShape && <AvatarBase className="shape" />}
				{icon}
			</figure>
		);
	},
	{
		target: 'Avatar-root',
		label: 'Avatar',
	},
)({
	margin: 0,
	padding: 0,
	width: '30px',
	height: '30px',
	userSelect: 'none',
	position: 'relative',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	'& .icon': {
		zIndex: 2,
		marginBottom: 0,
		width: '70%',
		height: 'auto',
		'& .st0': {
			fill: 'var(--message__avatar__icon_color)',
		},
	},
	'& svg.icon': {
		height: '70%',
	},
	'& .shape': {
		position: 'absolute',
		height: '100%',
		width: '100%',
		zIndex: 1,
		'& .st0': {
			fill: 'var(--message__avatar__shape_color)',
		},
	},
	'&.message__avatar--user': {
		color: 'var(--message__avatar--user__icon_color)',
		'& .shape': {
			'& .st0': {
				fill: 'var(--message__avatar--user__shape_color)',
			},
		},
		'& .icon': {
			'& .st0': {
				fill: 'var(--message__avatar--user__icon_color)',
			},
		},
	},
	'&.message__avatar--agent': {
		'& .shape': {
			'& .st0': {
				fill: 'var(--message__avatar--agent__shape_color)',
			},
		},
		'& .icon': {
			'& .st0': {
				fill: 'var(--message__avatar--agent__icon_color)',
			},
		},
	},
	'&.message__avatar--system': {
		'& .shape': {
			'& .st0': {
				fill: 'var(--message__avatar--system__shape_color)',
			},
		},
		'& .icon': {
			'& .st0': {
				fill: 'var(--message__avatar--system__icon_color)',
			},
		},
	},
	'& img.icon': {
		width: '100%',
		height: 'auto',
		stroke: 'none',
	},
	// When the shape is visible, custom images are sized to 70 % so the
	// rounded shape frame is visible around the image (same as SVG icons).
	'& img.icon--framed': {
		width: '70%',
	},
});

// ── accessories ──────────────────────────────────────────────────────────────

/**
 * Resolves whether the avatar background shape should be hidden for a sender.
 *
 * Applies in both default and custom (`*_logo` string) modes.
 *
 * @param sender - The message sender whose shape flag should be checked.
 * @param flags - The portion of the chat config containing the shape hide flags.
 * @returns `true` when the shape should be omitted for this sender.
 */
const shouldHideAvatarShape = (
	sender: SenderSource,
	flags: {
		hide_user_avatar_shape?: boolean;
		hide_agent_avatar_shape?: boolean;
		hide_system_avatar_shape?: boolean;
	},
): boolean => {
	if (sender === SenderSource.USER)
		return flags.hide_user_avatar_shape === true;
	if (sender === SenderSource.AGENT)
		return flags.hide_agent_avatar_shape === true;
	if (sender === SenderSource.SYSTEM)
		return flags.hide_system_avatar_shape === true;
	return false;
};

/**
 * Resolves whether the built-in avatar icon should be hidden for a sender.
 *
 * Applies only in default mode (`*_logo` is `undefined`). When `*_logo` is a
 * string the icon is always the custom image regardless of this flag.
 *
 * @param sender - The message sender whose icon flag should be checked.
 * @param flags - The portion of the chat config containing the icon hide flags.
 * @returns `true` when the built-in icon should be omitted for this sender.
 */
const shouldHideAvatarIcon = (
	sender: SenderSource,
	flags: {
		hide_user_avatar_icon?: boolean;
		hide_agent_avatar_icon?: boolean;
		hide_system_avatar_icon?: boolean;
	},
): boolean => {
	if (sender === SenderSource.USER) return flags.hide_user_avatar_icon === true;
	if (sender === SenderSource.AGENT)
		return flags.hide_agent_avatar_icon === true;
	if (sender === SenderSource.SYSTEM)
		return flags.hide_system_avatar_icon === true;
	return false;
};
